import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Menu, X } from "lucide-react";

import { SHOPIFY_APP_STORE_URL } from "./constants";

/* === MobileNavLink — single item in the slide-down sheet === */
export const MobileNavLink = ({ href, testid, onClick, active, children }) => (
  <a
    href={href}
    data-testid={testid}
    onClick={onClick}
    className={`flex items-center justify-between px-4 py-3 rounded-xl text-base font-semibold active:bg-slate-100 transition ${
      active ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200" : "text-slate-700"
    }`}
  >
    <span>{children}</span>
    {active && <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" aria-hidden />}
  </a>
);

/* === Nav — sticky top nav with desktop links + mobile hamburger sheet === */
export const Nav = ({ scrolled, mobileNavOpen, setMobileNavOpen, activeSection }) => (
  <header
    data-testid="landing-nav"
    className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
      scrolled || mobileNavOpen ? "backdrop-blur-xl bg-white/95 border-b border-slate-200 shadow-sm" : "bg-transparent"
    }`}
  >
    <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-10 h-14 sm:h-16 flex items-center justify-between gap-2">
      <Link to="/" data-testid="nav-logo" className="flex items-center gap-2 sm:gap-3 min-w-0">
        <img src="/logo.png" alt="AI Growth Operator" className="w-8 h-8 sm:w-9 sm:h-9 rounded-lg object-contain shrink-0" />
        <span className="text-sm font-bold text-slate-900 truncate">AI Growth Operator</span>
      </Link>
      <nav className="hidden md:flex items-center gap-8 text-sm font-medium text-slate-600">
        <a href="#overview" data-testid="nav-overview" className={`hover:text-slate-900 transition ${activeSection==="overview"?"text-emerald-700 font-semibold":""}`}>Demo</a>
        <a href="#problem" data-testid="nav-problem" className={`hover:text-slate-900 transition ${activeSection==="problem"?"text-emerald-700 font-semibold":""}`}>Problem</a>
        <a href="#features" data-testid="nav-features" className={`hover:text-slate-900 transition ${activeSection==="features"?"text-emerald-700 font-semibold":""}`}>Features</a>
        <a href="#proof" data-testid="nav-proof" className={`hover:text-slate-900 transition ${activeSection==="proof"?"text-emerald-700 font-semibold":""}`}>Proof</a>
        <a href="#pricing" data-testid="nav-pricing" className={`hover:text-slate-900 transition ${activeSection==="pricing"?"text-emerald-700 font-semibold":""}`}>Pricing</a>
      </nav>
      <div className="flex items-center gap-2">
        <a href={SHOPIFY_APP_STORE_URL} target="_blank" rel="noopener noreferrer"
           data-testid="nav-app-store-cta"
           className="hidden sm:inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-semibold transition shadow-sm shadow-emerald-500/30">
          Get on Shopify <ArrowUpRight className="w-3.5 h-3.5" />
        </a>
        {/* Mobile-only hamburger */}
        <button data-testid="nav-hamburger"
                onClick={() => setMobileNavOpen((o) => !o)}
                aria-label={mobileNavOpen ? "Close menu" : "Open menu"}
                aria-expanded={mobileNavOpen}
                className="md:hidden inline-flex items-center justify-center w-10 h-10 rounded-full border border-slate-200 text-slate-700 active:bg-slate-100 transition">
          {mobileNavOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>
    </div>

    {/* Mobile slide-down sheet */}
    <div
      data-testid="mobile-nav-sheet"
      className={`md:hidden overflow-hidden transition-[max-height,opacity] duration-300 ease-out border-t border-slate-100 ${
        mobileNavOpen ? "max-h-[480px] opacity-100" : "max-h-0 opacity-0"
      }`}
    >
      <div className="bg-white/95 backdrop-blur-xl px-4 py-3 space-y-1">
        <MobileNavLink href="#overview" testid="mnav-overview" active={activeSection==="overview"} onClick={() => setMobileNavOpen(false)}>Demo</MobileNavLink>
        <MobileNavLink href="#problem" testid="mnav-problem" active={activeSection==="problem"} onClick={() => setMobileNavOpen(false)}>Problem</MobileNavLink>
        <MobileNavLink href="#features" testid="mnav-features" active={activeSection==="features"} onClick={() => setMobileNavOpen(false)}>Features</MobileNavLink>
        <MobileNavLink href="#proof" testid="mnav-proof" active={activeSection==="proof"} onClick={() => setMobileNavOpen(false)}>Proof</MobileNavLink>
        <MobileNavLink href="#pricing" testid="mnav-pricing" active={activeSection==="pricing"} onClick={() => setMobileNavOpen(false)}>Pricing</MobileNavLink>
        <div className="pt-3 mt-2 border-t border-slate-100 grid gap-2">
          <a href={SHOPIFY_APP_STORE_URL} target="_blank" rel="noopener noreferrer"
             data-testid="mnav-app-store-cta"
             onClick={() => setMobileNavOpen(false)}
             className="inline-flex items-center justify-center gap-1.5 px-4 py-3 rounded-full bg-emerald-500 active:bg-emerald-600 text-white text-sm font-bold shadow-sm shadow-emerald-500/30">
            Install free on Shopify · 60 sec <ArrowUpRight className="w-4 h-4" />
          </a>
          <a href="/#overview"
             data-testid="mnav-demo"
             onClick={() => setMobileNavOpen(false)}
             className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full border border-slate-300 text-slate-700 text-sm font-semibold active:bg-slate-50">
            Watch the 45s Overview
          </a>
        </div>
      </div>
    </div>
  </header>
);

export default Nav;
